/**
 * Vocabulário único de status de ATF — rótulo, cor e toda ação disponível.
 *
 * As REGRAS (podeReenviar/podeExcluir/podeCancelar/emAndamento/revalidar)
 * portam `atf_status.dart` (App Tresco), a fonte canônica das *ações* por
 * status. A cor é o PAPEL do `Selo` da biblioteca (0094), o mesmo do resto do
 * painel: o handoff da 0034 pintava erro e "a verificar" de preto, e a cor
 * sozinha não dizia a diferença entre os dois.
 *
 * `revalidar` só existe em ERRO: é o único status que o backend reconcilia
 * sozinho (a ATF pode ter sido achada no portal, ou conciliada manualmente).
 * Não é o mesmo que `emAndamento` (fila) — fundir os dois anunciaria
 * "PROCESSANDO" sobre uma recusa real do DER.
 *
 * Status vazio/nulo cai em PENDENTE (allowlist, nunca negação — o gate de
 * exclusão já foi uma negação por engano: `status != 'EMITIDA'` liberava
 * INDETERMINADA sem querer). Status desconhecido-mas-presente mostra o
 * próprio valor cru, sempre consulta-somente.
 */

import type { Papel } from '../../../common/interface';

export type AtfStatus =
  | 'PENDENTE' | 'PROCESSANDO' | 'EMITIDA' | 'ERRO' | 'CANCELADA' | 'INDETERMINADA';

export interface AtfStatusInfo {
  status: string;
  rotulo: string;
  papel: Papel;
  podeReenviar: boolean;
  podeExcluir: boolean;
  podeCancelar: boolean;
  emAndamento: boolean;
  revalidar: boolean;
}

const TABELA: Record<AtfStatus, Omit<AtfStatusInfo, 'status'>> = {
  PENDENTE: {
    rotulo: 'PENDENTE', papel: 'neutro',
    podeReenviar: false, podeExcluir: true, podeCancelar: false,
    emAndamento: true, revalidar: false,
  },
  PROCESSANDO: {
    rotulo: 'PROCESSANDO', papel: 'info',
    podeReenviar: false, podeExcluir: false, podeCancelar: false,
    emAndamento: true, revalidar: false,
  },
  EMITIDA: {
    rotulo: 'EMITIDA', papel: 'sucesso',
    podeReenviar: false, podeExcluir: false, podeCancelar: true,
    emAndamento: false, revalidar: false,
  },
  ERRO: {
    rotulo: 'ERRO', papel: 'perigo',
    podeReenviar: true, podeExcluir: true, podeCancelar: false,
    emAndamento: false, revalidar: true,
  },
  CANCELADA: {
    rotulo: 'CANCELADA', papel: 'neutro',
    podeReenviar: false, podeExcluir: true, podeCancelar: false,
    emAndamento: false, revalidar: false,
  },
  INDETERMINADA: {
    rotulo: 'A VERIFICAR', papel: 'atencao',
    podeReenviar: false, podeExcluir: false, podeCancelar: false,
    emAndamento: false, revalidar: false,
  },
};

const STATUS_CONHECIDOS = Object.keys(TABELA) as AtfStatus[];

/**
 * `chPlc` é dado do ITEM, não do status: sem a chave do portal, o backend
 * recusa cancelamento com 422 mesmo em EMITIDA. Quem oferece o botão precisa
 * conferir os dois — `info.podeCancelar && chPlc != null` — nunca só o status.
 */
export function atfStatusInfo(statusBruto: string | null | undefined, chPlc?: string | null): AtfStatusInfo {
  const status = (statusBruto ?? '').trim().toUpperCase();

  if (!status) {
    return { status: 'PENDENTE', ...TABELA.PENDENTE };
  }

  if (STATUS_CONHECIDOS.includes(status as AtfStatus)) {
    const info = { status, ...TABELA[status as AtfStatus] };
    if (status === 'EMITIDA' && !chPlc) {
      return { ...info, podeCancelar: false };
    }
    return info;
  }

  // Desconhecido: allowlist, não negação — mostra o valor cru, sempre
  // consulta-somente.
  return {
    status, rotulo: status, papel: 'neutro',
    podeReenviar: false, podeExcluir: false, podeCancelar: false,
    emAndamento: false, revalidar: false,
  };
}

/**
 * Os filtros da tela, agrupados como o desenho pede (specs/0035 design/ §2).
 *
 * "Em andamento" cobre PENDENTE **e** PROCESSANDO: para quem espera a
 * autorização, a diferença entre "na fila" e "sendo processada" é do sistema,
 * não dele. Cada opção carrega os status crus que envia ao servidor.
 */
export const FILTROS_STATUS: Array<{ valor: string; rotulo: string; status?: string[] }> = [
  { valor: 'TODOS', rotulo: 'Todas' },
  { valor: 'EM_ANDAMENTO', rotulo: 'Em andamento', status: ['PENDENTE', 'PROCESSANDO'] },
  { valor: 'EMITIDA', rotulo: 'Emitidas', status: ['EMITIDA'] },
  { valor: 'ERRO', rotulo: 'Com erro', status: ['ERRO'] },
  { valor: 'INDETERMINADA', rotulo: 'A verificar', status: ['INDETERMINADA'] },
  // Cancelada não está no desenho, mas existe em produção e o app a lista —
  // esconder o filtro não faz a ATF cancelada sumir da lista.
  { valor: 'CANCELADA', rotulo: 'Canceladas', status: ['CANCELADA'] },
];

/** Os status crus de um filtro da tela; vazio = sem recorte. */
export function statusDoFiltro(valor: string): string[] {
  return FILTROS_STATUS.find(f => f.valor === valor)?.status ?? [];
}

/**
 * Janelas de período do desenho. `null` em `dias` = o ano corrente, que não é
 * uma janela deslizante e por isso não cabe em "últimos N dias".
 */
export const FILTROS_PERIODO: Array<{ valor: string; rotulo: string; dias: number | null }> = [
  { valor: 'MES', rotulo: 'Este mês', dias: 0 },
  { valor: '30', rotulo: 'Últimos 30 dias', dias: 30 },
  { valor: '90', rotulo: 'Últimos 90 dias', dias: 90 },
  { valor: 'ANO', rotulo: 'Este ano', dias: null },
];

/**
 * Converte a janela escolhida em datas, **em horário de Brasília**.
 *
 * `toISOString()` aqui produziria o dia errado depois das 21h — é a mesma
 * armadilha que a apuração de fatura já pagou (`src/utils/brtDate.ts`).
 */
export function periodoParaDatas(valor: string): { dataInicio?: string; dataFim?: string } {
  const agoraBRT = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const hoje = iso(agoraBRT);

  const opcao = FILTROS_PERIODO.find(f => f.valor === valor);
  if (!opcao) return {};

  if (opcao.valor === 'MES') {
    return { dataInicio: iso(new Date(agoraBRT.getFullYear(), agoraBRT.getMonth(), 1)), dataFim: hoje };
  }
  if (opcao.dias === null) {
    return { dataInicio: `${agoraBRT.getFullYear()}-01-01`, dataFim: hoje };
  }
  const inicio = new Date(agoraBRT);
  inicio.setDate(inicio.getDate() - opcao.dias);
  return { dataInicio: iso(inicio), dataFim: hoje };
}
